/**
 * Done. / ChiroCPD — Cap Status Context
 *
 * Fetches /api/cap-status on mount and registers a listener on api.ts so
 * any mutation that returns a capStatus sidecar automatically updates this context.
 *
 * Provides: cap hours remaining, AI generation count, cap-reached flags.
 * Used by: CapIndicator, ActivityForm (hour guard), AIReflectionPanel (AI guard).
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { api, setCapStatusListener, clearCapStatusListener } from '@/lib/api';
import type { CapStatus } from '@/lib/types';

// ── Context shape ─────────────────────────────────────────────────────────────

interface CapStatusContextValue {
  cap: CapStatus | null;
  loading: boolean;
  error: string | null;
  refreshCapStatus: () => Promise<void>;
  /** Called automatically by api.ts sidecar — updates context from mutation response */
  updateFromResponse: (cap: CapStatus) => void;
}

const CapStatusContext = createContext<CapStatusContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────────

interface CapStatusProviderProps {
  children: ReactNode;
}

export function CapStatusProvider({ children }: CapStatusProviderProps): React.ReactElement {
  const [cap, setCap] = useState<CapStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateFromResponse = useCallback((incoming: CapStatus): void => {
    setCap(incoming);
  }, []);

  const fetchCapStatus = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const status = await api.get<CapStatus>('/api/cap-status');
      setCap(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cap status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Register the sidecar listener on mount so mutation responses auto-update cap
  useEffect(() => {
    setCapStatusListener(updateFromResponse);
    return () => {
      clearCapStatusListener();
    };
  }, [updateFromResponse]);

  // Initial fetch
  useEffect(() => {
    void fetchCapStatus();
  }, [fetchCapStatus]);

  const value: CapStatusContextValue = {
    cap,
    loading,
    error,
    refreshCapStatus: fetchCapStatus,
    updateFromResponse,
  };

  return (
    <CapStatusContext.Provider value={value}>{children}</CapStatusContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useCapStatusContext(): CapStatusContextValue {
  const ctx = useContext(CapStatusContext);
  if (!ctx) {
    throw new Error('useCapStatusContext must be used within a CapStatusProvider');
  }
  return ctx;
}
